import { scooterDepositPercent } from '@/lib/depositRates';
import React, { useState } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Star, MapPin, Shield, Truck, BadgeCheck, ArrowLeft, Check, X,
  Fuel, Palette, Calendar as CalendarIcon, Cog, Sparkles, User, ShieldCheck,
  Info, Heart, TrendingDown, PartyPopper
} from 'lucide-react';
import { Header } from '@/components/Header';
import { SEO } from '@/components/SEO';
import { ShareListingButton } from '@/components/ShareListingButton';
import { MobileBookingBar } from '@/components/MobileBookingBar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { BookingCalendar } from '@/components/BookingCalendar';
import { formatTHB } from '@/lib/pricing';
import { useScooterQuote } from '@/lib/useScooterQuote';
import { PriceSummary, DiscountBanner } from '@/components/PriceSummary';
import { formatPct } from '@/lib/scooterDiscounts';
import { TimezoneNote } from '@/lib/timezone';
import { formatDateOnly } from '@/lib/dateFR';
import { pickTranslation } from '@/i18n/pickTranslation';
import { translateFuelPolicy, translateColor } from '@/i18n/translateStaticField';
import { ReviewComment } from '@/components/ReviewComment';
import { translateServerError } from '@/lib/serverErrors';
import { numberLocale } from '@/lib/format';
import ListingGallery from '@/components/ListingGallery';
import { BookingRequestSentDialog } from '@/components/BookingRequestSentDialog';
import { grandTotal } from '@/lib/deliveryFees';
import { paymentNoteLines } from '@/lib/paymentNote';
import { cleanMapsUrl } from '@/lib/transferRoute';
import { localizedHref } from '@/i18n/urlLanguage';
import { businessNameOf, neutralProviderName, publicProviderName } from '@/lib/providerName';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABEL_KEYS = {
  urbain: 'scooters.categories.urbain',
  confort: 'scooters.categories.confort',
  maxi: 'scooters.categories.maxi',
  premium: 'scooters.categories.premium',
};

const SpecItem = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 p-4 rounded-xl bg-background-soft/50">
    <Icon className="w-5 h-5 text-accent shrink-0 mt-0.5" />
    <div>
      <div className="text-xs text-ink-muted uppercase tracking-wide">{label}</div>
      <div className="text-sm font-semibold text-primary mt-0.5">{value}</div>
    </div>
  </div>
);

const BadgeChip = ({ children, tone = 'default' }) => {
  const tones = {
    default: 'bg-background-soft text-primary',
    accent: 'bg-accent/10 text-accent',
    warn: 'bg-amber-50 text-amber-700',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium ${tones[tone]}`}>
      {children}
    </span>
  );
};

// Génération des créneaux horaires (07h00 → 21h00, pas 30min)
const HOUR_SLOTS = [];
for (let h = 7; h <= 21; h++) {
  HOUR_SLOTS.push(`${String(h).padStart(2, '0')}:00`);
  if (h < 21) HOUR_SLOTS.push(`${String(h).padStart(2, '0')}:30`);
}

// Calcul du nombre de jours (autoritaire, aligné avec le backend).
// Format a Date into YYYY-MM-DD using LOCAL calendar components (avoids UTC shift bug).
const toLocalYMD = (d) => {
  if (!d) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Règles (voir server.compute_rental_days) :
//   • Même jour + heure retour  <  heure prise → invalide (retourne 0)
//   • Même jour + heure retour >= heure prise → 1 jour
//   • Jours différents : base = (fin - début) ; si heure retour > heure prise → +1 jour
export const computeDays = (startDate, startTime, endDate, endTime) => {
  if (!startDate || !endDate || !startTime || !endTime) return 0;
  const d1 = new Date(`${startDate}T00:00:00`);
  const d2 = new Date(`${endDate}T00:00:00`);
  if (isNaN(d1) || isNaN(d2) || d2 < d1) return 0;
  const [sh, sm] = startTime.split(':').map((x) => parseInt(x, 10));
  const [eh, em] = endTime.split(':').map((x) => parseInt(x, 10));
  const startMin = sh * 60 + sm;
  const endMin = eh * 60 + em;
  if (d1.getTime() === d2.getTime()) {
    if (endMin < startMin) return 0;
    return 1;
  }
  const baseDays = Math.round((d2.getTime() - d1.getTime()) / (24 * 60 * 60 * 1000));
  if (endMin > startMin) return baseDays + 1;
  return baseDays;
};

/**
 * Map a backend service document (provider-created listings live only in DB —
 * not in `/data/scooters.js`) into the same shape the ScooterDetail page renders,
 * so providers can preview their own drafts / pending / corrections listings.
 */
const dbServiceToScooter = (svc, lang = 'fr') => {
  const d = svc.details || {};
  const sec = d.security_deposit || {};
  // Caution: prefer new field; fall back to legacy `deposit` (fixed THB).
  const securityDeposit = ['none', 'fixed', 'passport'].includes((sec.type || '').toLowerCase())
    ? { type: sec.type, amount: Number(sec.amount) || 0 }
    : ((d.deposit_type === 'fixed' && d.deposit)
        ? { type: 'fixed', amount: Number(d.deposit) || 0 }
        : (d.deposit && d.deposit_type !== 'percentage')
          ? { type: 'fixed', amount: Number(d.deposit) || 0 }
          : { type: 'none', amount: 0 });
  // Acompte de réservation: exactly the rate the server applies (see scooterDepositPercent).
  const acomptePct = scooterDepositPercent(svc);
  return {
    id: svc.service_id,
    name: pickTranslation(svc, 'title', lang) || svc.title || d.model || 'Annonce',
    brand: d.brand || '',
    category: d.category || svc.category || 'urbain',
    price: svc.price || 0,
    zone: svc.zone || svc.location || '',
    rating: 0,
    reviews: 0,
    year: d.year || new Date().getFullYear(),
    color: d.color || '—',
    engine: d.engine || 125,
    minRentalDuration: Math.max(1, Math.min(30, parseInt(d.minimum_rental_duration) || 1)),
    helmets: d.helmets ?? 2,
    insurance: d.insurance ?? true,
    // Delivery: only with the provider's fees by zone (same for all their scooters).
    delivery: Array.isArray(svc.delivery_rates) && svc.delivery_rates.length > 0,
    deliveryRates: Array.isArray(svc.delivery_rates) ? svc.delivery_rates : [],
    freeDelivery: false,
    deposit: securityDeposit.amount,
    securityDeposit,
    bookingDepositPercent: acomptePct,
    passport: d.passport_required ?? false,
    fuelPolicy: d.fuel_policy || 'Rendu plein',
    provider: {
      id: svc.provider_id || null,
      name: publicProviderName(svc, lang),                      // commercial name, else a neutral label — never the account holder's name
      businessName: businessNameOf(svc),                        // the commercial name only (JSON-LD, SEO text, provider share)
      verified: !!svc.provider_verified,
      joinedYear: new Date().getFullYear(),
    },
    priceTiers: svc.price_tiers || [],
    // Discounts that really apply (computed by the server), for the banner.
    durationDiscounts: svc.duration_discounts || null,
    description: pickTranslation(svc, 'description', lang) || svc.description || '',
    images: (svc.images && svc.images.length ? svc.images : ['https://customer-assets.emergentagent.com/job_journey-link-13/artifacts/j9icv8bt_Image.png']),
    _status: svc.status,
    _paused: svc.paused,
    _archived: svc.archived,
  };
};

export const ScooterDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const staticScooter = null; // Static demo fallback retired — all scooters come from MongoDB
  const [dbScooter, setDbScooter] = useState(null);
  const [dbLoading, setDbLoading] = useState(true);
  // Normalise the scooter shape so both static & DB objects expose
  // securityDeposit + bookingDepositPercent (see dbServiceToScooter).
  const rawScooter = staticScooter || dbScooter;
  const scooter = React.useMemo(() => {
    if (!rawScooter) return null;
    if (rawScooter.securityDeposit && rawScooter.bookingDepositPercent !== undefined) return rawScooter;
    return {
      ...rawScooter,
      securityDeposit: rawScooter.securityDeposit
        || (rawScooter.deposit ? { type: 'fixed', amount: rawScooter.deposit } : { type: 'none', amount: 0 }),
      bookingDepositPercent: rawScooter.bookingDepositPercent ?? 0,
    };
  }, [rawScooter]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [authPromptOpen, setAuthPromptOpen] = useState(false);
  const [createdBookingId, setCreatedBookingId] = useState(null);
  // Period searched on /scooters (?start_date=…): pre-fills the booking form.
  const [periodParams] = useSearchParams();
  const qDate = (k) => (/^\d{4}-\d{2}-\d{2}$/.test(periodParams.get(k) || '') ? periodParams.get(k) : '');
  const qTime = (k) => (/^\d{2}:\d{2}$/.test(periodParams.get(k) || '') ? periodParams.get(k) : '12:00');
  const [startDate, setStartDate] = useState(() => qDate('start_date'));
  const [startTime, setStartTime] = useState(() => qTime('start_time'));
  const [endDate, setEndDate] = useState(() => qDate('end_date'));
  const [endTime, setEndTime] = useState(() => qTime('end_time'));
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [bookingNotes, setBookingNotes] = useState('');
  const [acceptCgv, setAcceptCgv] = useState(false);
  const [cgvOpen, setCgvOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dbReviews, setDbReviews] = useState([]);
  const [reviewSummary, setReviewSummary] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [blockedDates, setBlockedDates] = useState([]);
  const [blockedPeriods, setBlockedPeriods] = useState([]);
  // Partially-unavailable dates (day has one or more bookings/blocks that do
  // NOT cover the entire day). Selectable but visually flagged so travelers
  // are informed BEFORE the form step. Full-day unavailability is merged in
  // `blockedDates` above so the DayPicker disables selection outright.
  const [partialDates, setPartialDates] = useState([]);
  const [deliveryRequested, setDeliveryRequested] = useState(false);
  const [deliveryHotel, setDeliveryHotel] = useState('');
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [deliveryZone, setDeliveryZone] = useState('');
  const [deliveryMaps, setDeliveryMaps] = useState('');

  // Public reviews: reloaded when the language changes (the API computes the neutral label « Voyageur vérifié » /
  // « Verified traveller » in that language) — and only these; see the other effects below.
  const reviewsLang = String(i18n.language || 'fr').toLowerCase().startsWith('en') ? 'en' : 'fr';
  React.useEffect(() => {
    if (!id) return undefined;
    let alive = true;
    axios.get(`${API}/reviews/service/${id}`, { params: { lang: reviewsLang } })
      .then((r) => { if (alive) setDbReviews(r.data || []); }).catch(() => {});
    return () => { alive = false; };
  }, [id, reviewsLang]);

  // Once per listing (NOT on a language change): rating summary, page view, availability.
  React.useEffect(() => {
    if (!id) return;
    axios.get(`${API}/reviews/summary/${id}`).then((r) => setReviewSummary(r.data)).catch(() => {});
    // Analytics — record a page view (fire and forget).
    axios.post(`${API}/services/${id}/track`, { event: 'view' }).catch(() => {});
    // Unified availability — single source of truth combining Ridalo bookings,
    // external bookings and provider blocks. Feeds the DayPicker so unavailable
    // dates are red + non-selectable BEFORE the traveler reaches the form.
    axios.get(`${API}/services/${id}/availability`)
      .then((r) => {
        setBlockedDates(r.data?.unavailable_dates || []);
        setPartialDates(r.data?.partial_dates || []);
        setBlockedPeriods(r.data?.periods || []);
      })
      .catch(() => { setBlockedDates([]); setPartialDates([]); setBlockedPeriods([]); });
  }, [id]);

  // Listing data (provider info, is_verified badge, legacy blocked_dates), localised: unchanged, still follows the language.
  // Credentials let a provider preview its own unpublished listing.
  React.useEffect(() => {
    if (!id) return;
    axios.get(`${API}/services/${id}`, { withCredentials: true })
      .then((r) => {
        if (!staticScooter && r.data) {
          setDbScooter(dbServiceToScooter(r.data, i18n.language));
        }
      })
      .catch(() => {})
      .finally(() => setDbLoading(false));
  }, [id, staticScooter, i18n.language]);

  // Helper: Normalise les URLs images pour JSON-LD (remplace Railway/Preview/Assets par ridalo.co)
  const normalizeImageUrlForJsonLd = (url) => {
    if (!url) return url;
    // Ne transformer que les URLs internes Ridalo/Emergent, garder les URLs externes (Pexels, etc.)
    if (url.includes('railway.app') || url.includes('emergentagent.com')) {
      return url
        .replace(/https?:\/\/ridalo-production\.up\.railway\.app/g, 'https://ridalo.co')
        .replace(/https?:\/\/[^/]*\.emergentagent\.com/g, 'https://ridalo.co');
    }
    return url; // Garder les URLs externes telles quelles (Pexels, etc.)
  };

  // JSON-LD Product + AggregateRating conditionnel + BreadcrumbList
  React.useEffect(() => {
    if (!scooter || !id) return;

    const productLD = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": t('seo.scooterDetail.ldName', { name: scooter.name, zone: scooter.zone }),
      "brand": {
        "@type": "Brand",
        "name": scooter.brand
      },
      "image": scooter.images.map(normalizeImageUrlForJsonLd),
      "description": scooter.description,
      "category": "Vehicle Rental",
      "offers": {
        "@type": "Offer",
        "price": scooter.price.toString(),
        "priceCurrency": "THB",
        "priceSpecification": {
          "@type": "UnitPriceSpecification",
          "price": scooter.price.toString(),
          "priceCurrency": "THB",
          "unitCode": "DAY",
          "unitText": t('seo.perDay')
        },
        "url": `https://ridalo.co/scooters/${id}`,
        ...(scooter.provider.businessName ? { "seller": {
          "@type": "Organization",
          "name": scooter.provider.businessName
        } } : {})
      }
    };

    // AggregateRating UNIQUEMENT si reviews réelles existent
    if (reviewSummary?.avg && dbReviews.length > 0) {
      productLD.aggregateRating = {
        "@type": "AggregateRating",
        "ratingValue": reviewSummary.avg.toString(),
        "reviewCount": dbReviews.length.toString(),
        "bestRating": "5",
        "worstRating": "1"
      };
    }

    const breadcrumbLD = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": t('seo.breadcrumb.home'),
          "item": "https://ridalo.co/"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": t('seo.breadcrumb.scooters'),
          "item": "https://ridalo.co/scooters"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": scooter.name,
          "item": `https://ridalo.co/scooters/${id}`
        }
      ]
    };

    const s1 = document.createElement('script');
    s1.type = 'application/ld+json';
    s1.text = JSON.stringify(productLD);
    document.head.appendChild(s1);

    const s2 = document.createElement('script');
    s2.type = 'application/ld+json';
    s2.text = JSON.stringify(breadcrumbLD);
    document.head.appendChild(s2);

    return () => {
      if (s1.parentNode) s1.remove();
      if (s2.parentNode) s2.remove();
    };
  }, [scooter?.id, id, reviewSummary, dbReviews.length, t]);


  React.useEffect(() => {
    if (!user || !user.capabilities && user.role !== 'client' || !id) return;
    // User has client capability if: (a) capabilities includes 'client', OR (b) role is 'client' (backward compat)
    const hasClientCap = (user.capabilities && user.capabilities.includes('client')) || user.role === 'client';
    if (!hasClientCap) return;
    axios.get(`${API}/favorites`, { withCredentials: true })
      .then((r) => setIsFavorite((r.data || []).some((f) => f.service_id === id)))
      .catch(() => {});
  }, [user, id]);

  // Pré-remplir avec les données utilisateur si connecté (avant tout early-return)
  React.useEffect(() => {
    if (user && dialogOpen) {
      setEmail((prev) => prev || user.email || '');
      if (user.name) {
        const parts = user.name.split(' ');
        setFirstName((prev) => prev || parts[0] || '');
        setLastName((prev) => prev || parts.slice(1).join(' ') || '');
      }
    }
  }, [user, dialogOpen]);

  // Draft persistence — restore any pending booking (dates, form fields) that
  // a guest submitted before we bounced them to login/signup. Must live above
  // any conditional early-return so the hook order stays stable.
  React.useEffect(() => {
    if (!id) return;
    const draftKey = `ridalo:bookingDraft:${id}`;
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const d = JSON.parse(raw);
      if (d.startDate) setStartDate(d.startDate);
      if (d.startTime) setStartTime(d.startTime);
      if (d.endDate) setEndDate(d.endDate);
      if (d.endTime) setEndTime(d.endTime);
      if (d.firstName) setFirstName(d.firstName);
      if (d.lastName) setLastName(d.lastName);
      if (d.email) setEmail(d.email);
      if (d.bookingNotes) setBookingNotes(d.bookingNotes);
      if (typeof d.acceptCgv === 'boolean') setAcceptCgv(d.acceptCgv);
      if (typeof d.deliveryRequested === 'boolean') setDeliveryRequested(d.deliveryRequested);
      if (d.deliveryHotel) setDeliveryHotel(d.deliveryHotel);
      if (d.deliveryNotes) setDeliveryNotes(d.deliveryNotes);
      if (d.deliveryZone) setDeliveryZone(d.deliveryZone);
      if (d.deliveryMaps) setDeliveryMaps(d.deliveryMaps);
      const hasClientCap = user && ((user.capabilities && user.capabilities.includes('client')) || user.role === 'client');
      if (hasClientCap) setDialogOpen(true);
    } catch { /* ignore corrupt draft */ }
    // Only run once per id / when auth state resolves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user?.role]);

  // Price for the selected dates: always the server quote (same engine as the
  // booking). No price while it loads or after an error — never a stale total.
  const quoteState = useScooterQuote(id, startDate, endDate, startTime, endTime);

  if (!scooter) {
    if (dbLoading) {
      return (
        <div className="min-h-screen bg-background">
          <Header />
          <div className="pt-32 text-center px-4" data-testid="scooter-loading">
            <p className="text-ink-light">{t('scooters.detail.loading')}</p>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-32 text-center px-4">
          <h1 className="text-2xl font-semibold text-primary mb-3">{t('scooters.detail.notFound')}</h1>
          <Link to="/scooters" className="btn-primary inline-flex">{t('scooters.detail.backToList')}</Link>
        </div>
      </div>
    );
  }

  const days = computeDays(startDate, startTime, endDate, endTime);

  const priceSummary = days > 0 && quoteState.status === 'ok' ? quoteState.summary : null;
  const totalPrice = priceSummary ? priceSummary.total : null;
  // Acompte shown exactly as the server records it: round(total × % / 100, 2).
  const depositAmount = totalPrice != null ? Math.round(totalPrice * scooter.bookingDepositPercent) / 100 : null;

  // Stars and score only with real published reviews (same rule as the
  // AggregateRating above) — never a « 0 (0 avis) » placeholder.
  const hasReviews = Boolean(reviewSummary?.avg) && dbReviews.length > 0;
  const displayAvg = reviewSummary?.avg;
  // Delivery is offered only with the provider's fees by zone (at least one
  // active zone): the zone is required and its round-trip price is added to
  // the total. Shown for information only — the server reads the price from
  // the provider's settings and freezes it.
  const deliveryRates = scooter.deliveryRates || [];
  const deliveryOffered = deliveryRates.length > 0;
  const deliveryPriced = deliveryOffered;
  const selectedRate = deliveryPriced ? deliveryRates.find((r) => r.zone_id === deliveryZone) || null : null;
  const deliveryActive = deliveryRequested && deliveryOffered;
  const deliveryFee = deliveryActive && selectedRate ? Number(selectedRate.fee) : null;
  const grandTotalPrice = totalPrice != null ? grandTotal(totalPrice, deliveryFee) : null;
  // Delivery form: zone and accommodation required; the Google Maps link is
  // optional (format and Google Maps domain only — never a location check).
  const mapsUrl = deliveryMaps.trim() ? cleanMapsUrl(deliveryMaps) : null;
  const mapsInvalid = Boolean(deliveryMaps.trim()) && !mapsUrl;
  const deliveryIncomplete = deliveryActive && (!selectedRate || !deliveryHotel.trim() || mapsInvalid);
  const displayCount = dbReviews.length;
  const combinedReviews = [
    ...dbReviews.map((r) => ({
      name: r.reviewer_name,
      rating: r.rating,
      comment: r.comment,
      comment_translations: r.comment_translations,
      comment_source_lang: r.comment_source_lang,
      date: r.created_at,
      verified: true,
    })),
  ];

  const toggleFavorite = async () => {
    if (!user) { toast.error(t('scooters.toasts.loginToFav')); return; }
    const hasClientCap = (user.capabilities && user.capabilities.includes('client')) || user.role === 'client';
    if (!hasClientCap) { toast.error(t('scooters.toasts.travelerOnly')); return; }
    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/${scooter.id}`, { withCredentials: true });
        setIsFavorite(false);
        toast.success(t('scooters.toasts.removedFromFav'));
      } else {
        await axios.post(`${API}/favorites`, {
          service_id: scooter.id,
          snapshot: { title: scooter.name, image: scooter.images?.[0], price: scooter.price, location: scooter.zone, category: 'scooter' },
        }, { withCredentials: true });
        setIsFavorite(true);
        toast.success(t('scooters.toasts.addedToFav'));
      }
    } catch { toast.error(t('scooters.toasts.error')); }
  };

  // Draft persistence helpers — so a guest bounced to login/signup returns
  // with their dates, notes and delivery info intact.
  const draftKey = id ? `ridalo:bookingDraft:${id}` : null;

  const saveDraft = () => {
    if (!draftKey) return;
    try {
      localStorage.setItem(draftKey, JSON.stringify({
        startDate, startTime, endDate, endTime,
        firstName, lastName, email, bookingNotes, acceptCgv,
        deliveryRequested, deliveryHotel, deliveryNotes, deliveryZone, deliveryMaps,
      }));
    } catch { /* quota / private mode — ignore */ }
  };

  const clearDraft = () => {
    if (!draftKey) return;
    try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
  };

  const minRentalDuration = Math.max(1, Math.min(30, parseInt(scooter.minRentalDuration) || 1));
  const meetsMinDuration = days >= minRentalDuration;
  const isFormValid = startDate && endDate && startTime && endTime && firstName.trim() && lastName.trim() && email.trim() && acceptCgv && days > 0 && meetsMinDuration
    && totalPrice != null && !deliveryIncomplete;

  const handleBooking = async () => {
    if (!isFormValid) return;
    if (!user) {
      // Guest — save current form state and open the auth prompt modal.
      saveDraft();
      setAuthPromptOpen(true);
      return;
    }
    // Travellers and providers can book (a provider books as a traveller with the same account).
    const caps = Array.isArray(user.capabilities) ? user.capabilities : [];
    if (!['client', 'provider'].some((r) => user.role === r || caps.includes(r))) {
      toast.error(t('scooters.detail.toasts.clientOnly'));
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/bookings`,
        {
          service_id: scooter.id,
          booking_date: startDate,
          end_date: endDate,
          start_time: startTime,
          end_time: endTime,
          // Never ask for a delivery the listing does not offer (e.g. an old draft).
          delivery_requested: deliveryRequested && deliveryOffered,
          delivery_details: (deliveryRequested && deliveryOffered) ? {
            hotel: deliveryHotel.trim() || null,
            google_maps_url: mapsUrl,
            notes: deliveryNotes.trim() || null,
            zone_id: deliveryZone || null,
          } : null,
          details: {
            scooter_name: scooter.name,
            provider: scooter.provider.name,
            start_date: startDate,
            start_time: startTime,
            end_date: endDate,
            end_time: endTime,
            days,
            total_price: totalPrice,
            first_name: firstName,
            last_name: lastName,
            email,
            notes: bookingNotes,
          },
        },
        { withCredentials: true }
      );
      // Only show success if the backend actually returned a booking reference.
      if (!data || !data.booking_id) {
        throw new Error(t('scooters.detail.toasts.noRef'));
      }
      setCreatedBookingId(data.booking_id);
      setDialogOpen(false);
      setConfirmOpen(true);
      clearDraft();
    } catch (error) {
      // Real error — keep the form open, keep user data, show the actual message.
      const msg =
        translateServerError(error?.response?.data?.detail) ||
        error?.message ||
        t('scooters.detail.toasts.bookingFailed');
      console.error('Booking creation failed', { status: error?.response?.status, data: error?.response?.data, msg });
      toast.error(typeof msg === 'string' ? msg : t('scooters.detail.toasts.bookingFailedShort'), { duration: 6000 });
    } finally {
      setSubmitting(false);
    }
  };

  // Zone used in SEO texts; island-wide labels read better as plain "Phuket".
  const seoZone = scooter.zone && scooter.zone !== 'Phuket' && !/^Toute /.test(scooter.zone) ? scooter.zone : null;

  return (
    <div className="min-h-screen bg-background" data-testid="scooter-detail">
      <SEO
        title={seoZone
          ? t('seo.scooterDetail.titleZone', { name: scooter.name, zone: seoZone })
          : t('seo.scooterDetail.title', { name: scooter.name })}
        description={(() => {
          const parts = [];
          parts.push(seoZone
            ? t('seo.scooterDetail.introZone', { name: scooter.name, zone: seoZone })
            : t('seo.scooterDetail.intro', { name: scooter.name }));
          if (scooter.price) parts.push(t('seo.scooterDetail.pricePerDay', { price: Number(scooter.price).toLocaleString(numberLocale()) }));
          if (scooter.provider?.businessName) parts.push(t('seo.scooterDetail.provider', { name: scooter.provider.businessName }));
          parts.push(t('seo.scooterDetail.bookOnline'));
          return parts.join(' ').slice(0, 300);
        })()}
        canonical={`https://ridalo.co/scooters/${id}`}
      />
      <Header />

      <div className="pt-20 lg:pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          {/* Provider preview banner — visible when viewing an unapproved listing (own draft/pending/corrections). */}
          {scooter._status && scooter._status !== 'approved' && (
            <div
              className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
              data-testid="scooter-preview-banner"
            >
              <p className="font-semibold">{t('scooters.detail.preview.banner')}</p>
              <p className="text-xs mt-0.5">
                {t('scooters.detail.preview.statusLabel')} <span className="font-semibold">
                  {scooter._status === 'draft' && t('scooters.detail.preview.status.draft')}
                  {scooter._status === 'pending' && t('scooters.detail.preview.status.pending')}
                  {scooter._status === 'corrections_needed' && t('scooters.detail.preview.status.correctionsNeeded')}
                  {scooter._status === 'suspended' && t('scooters.detail.preview.status.suspended')}
                  {scooter._status === 'rejected' && t('scooters.detail.preview.status.rejected')}
                </span>
                {' '}· {t('scooters.detail.preview.notVisible')}
              </p>
            </div>
          )}

          {/* Breadcrumb — respects navigation history (provider fleet → listing → back → fleet) */}
          <div className="mb-6 flex items-center gap-2 text-sm text-ink-light">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-1 hover:text-accent transition-colors max-sm:min-h-11"
              data-testid="back-to-list"
            >
              <ArrowLeft className="w-4 h-4" /> {t('scooters.detail.back')}
            </button>
          </div>

          {/* Title header */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-primary" data-testid="scooter-title">
                  {scooter.name}
                </h1>
                <div className="flex flex-wrap items-center gap-3 mt-3">
                  {hasReviews && (
                    <>
                      <div className="flex items-center gap-1" data-testid="scooter-rating">
                        <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                        <span className="text-sm font-semibold text-primary">{displayAvg}</span>
                        <span className="text-sm text-ink-light">{t('scooters.detail.reviewsCount', { count: displayCount })}</span>
                      </div>
                      <span className="text-ink-muted">•</span>
                    </>
                  )}
                  <div className="inline-flex items-center gap-1 text-sm text-ink-light">
                    <MapPin className="w-4 h-4 text-accent" /> {t('scooters.detail.zonePrefix', { zone: scooter.zone })}
                  </div>
                  <span className="text-ink-muted">•</span>
                  <span className="text-sm text-ink-light">{t(CATEGORY_LABEL_KEYS[scooter.category] || 'scooters.categories.urbain')}</span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={toggleFavorite}
                  className={`inline-flex items-center gap-2 rounded-full px-4 py-2 border transition-all ${isFavorite ? 'bg-red-500 text-white border-red-500' : 'bg-white text-primary border-slate-200 hover:border-accent'}`}
                  data-testid="detail-fav-toggle"
                >
                  <Heart className={`w-4 h-4 ${isFavorite ? 'fill-current' : ''}`} />
                  <span className="text-sm font-medium">{isFavorite ? t('scooters.detail.favoriteOn') : t('scooters.detail.favoriteAdd')}</span>
                </button>
                <ShareListingButton category="scooter" listingId={scooter.id} title={scooter.name} />
              </div>
            </div>
          </div>

          {/* Gallery */}
          <ListingGallery images={scooter.images} alt={scooter.name} />

          {/* Badges row */}
          <div className="flex flex-wrap gap-2 mb-8" data-testid="scooter-badges">
            {scooter.provider.verified && (
              <BadgeChip tone="accent">
                <BadgeCheck className="w-3.5 h-3.5" /> {t('scooters.detail.verifiedProvider')}
              </BadgeChip>
            )}
            <BadgeChip>{t('scooters.detail.badges.helmetsIncluded', { count: scooter.helmets })}</BadgeChip>

            {scooter.freeDelivery ? (
              <BadgeChip tone="accent"><Truck className="w-3.5 h-3.5" /> {t('scooters.detail.badges.freeDelivery')}</BadgeChip>
            ) : scooter.delivery ? (
              <BadgeChip><Truck className="w-3 h-3" /> {t('scooters.detail.badges.deliveryAvailable')}</BadgeChip>
            ) : null}
            <BadgeChip>📍 {scooter.zone}</BadgeChip>
            {scooter.passport && <BadgeChip tone="warn">{t('scooters.detail.badges.passportRequired')}</BadgeChip>}
            {scooter.securityDeposit?.type === 'fixed' && scooter.securityDeposit.amount > 0 && (
              <BadgeChip>{t('scooters.detail.badges.securityDeposit', { amount: Number(scooter.securityDeposit.amount).toLocaleString(numberLocale()) })}</BadgeChip>
            )}
            <BadgeChip tone="accent">{scooter.bookingDepositPercent > 0 ? t('scooters.detail.badges.bookingDeposit', { pct: scooter.bookingDepositPercent }) : t('scooters.detail.badges.noBookingDeposit')}</BadgeChip>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left column */}
            <div className="lg:col-span-2 space-y-8">
              {/* Provider */}
              <div className="card-dashboard flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-accent flex items-center justify-center text-white font-bold text-lg max-sm:shrink-0">
                  {(scooter.provider.businessName || neutralProviderName())[0]}
                </div>
                <div className="flex-1 max-sm:min-w-0">
                  <div className="flex items-center justify-between gap-4 max-sm:flex-wrap max-sm:gap-x-3 max-sm:gap-y-1">
                    <div className="flex items-center gap-2 max-sm:min-w-0">
                      <h3 className="text-lg font-semibold text-primary max-sm:break-words">{scooter.provider.businessName || neutralProviderName()}</h3>
                      {scooter.provider.verified && <BadgeCheck className="w-5 h-5 text-accent" />}
                    </div>
                    {scooter.provider.id && (
                      <a
                        href={localizedHref(`/provider/${scooter.provider.id}`)}
                        className="text-sm text-accent hover:text-accent/80 transition-colors whitespace-nowrap max-sm:whitespace-normal flex items-center gap-1"
                        data-testid="provider-all-listings-link"
                      >
                        {t('scooters.detail.providerAllListings')}
                        <span aria-hidden="true">→</span>
                      </a>
                    )}
                    {scooter.provider.id && (
                      <ShareListingButton category="provider" listingId={scooter.provider.id}
                                          title={scooter.provider.businessName} variant="link" />
                    )}
                  </div>
                  <p className="text-sm text-ink-light">{t('scooters.detail.providerSince', { year: scooter.provider.joinedYear })}</p>
                </div>
              </div>

              {/* Description */}
              <section className="card-dashboard">
                <h2 className="text-xl font-bold text-primary mb-3">{t('scooters.detail.sections.about')}</h2>
                <p className="text-base text-ink-light leading-relaxed" data-testid="scooter-description">
                  {scooter.description}
                </p>
              </section>

              {/* Specs */}
              <section className="card-dashboard">
                <h2 className="text-xl font-bold text-primary mb-4">{t('scooters.detail.sections.specs')}</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <SpecItem icon={CalendarIcon} label={t('scooters.detail.specs.year')} value={scooter.year} />
                  <SpecItem icon={Palette} label={t('scooters.detail.specs.color')} value={translateColor(scooter.color, i18n.language)} />
                  <SpecItem icon={Cog} label={t('scooters.detail.specs.engine')} value={t('scooters.detail.specs.engineValue', { cc: scooter.engine })} />
                  <SpecItem
                    icon={CalendarIcon}
                    label={t('scooters.detail.specs.minDuration')}
                    value={t('scooters.detail.specs.minDurationValue', { count: minRentalDuration })}
                  />
                  <SpecItem icon={Sparkles} label={t('scooters.detail.specs.transmission')} value={t('scooters.detail.specs.transmissionValue')} />
                  <SpecItem icon={User} label={t('scooters.detail.specs.helmets')} value={t('scooters.detail.specs.helmetsValue', { count: scooter.helmets })} />
                  <SpecItem icon={Fuel} label={t('scooters.detail.specs.fuel')} value={translateFuelPolicy(scooter.fuelPolicy, i18n.language)} />
                </div>
              </section>

              {/* Conditions */}
              <section className="card-dashboard">
                <h2 className="text-xl font-bold text-primary mb-4">{t('scooters.detail.sections.conditions')}</h2>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    {scooter.delivery ? <Check className="w-5 h-5 text-accent shrink-0 mt-0.5" /> : <X className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />}
                    <span className="text-sm text-primary">
                      <span className="font-semibold">{t('scooters.detail.cond.deliveryLabel')}</span> — {scooter.freeDelivery ? t('scooters.detail.cond.deliveryFree') : scooter.delivery ? t('scooters.detail.cond.deliveryPaid') : t('scooters.detail.cond.deliveryNone')}
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ShieldCheck className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                    <span className="text-sm text-primary">
                      <span className="font-semibold">{t('scooters.detail.cond.cautionLabel')}</span> — {scooter.securityDeposit?.type === 'fixed' && scooter.securityDeposit.amount > 0
                        ? t('scooters.detail.cond.cautionValue', { amount: Number(scooter.securityDeposit.amount).toLocaleString(numberLocale()) })
                        : scooter.securityDeposit?.type === 'passport'
                        ? t('scooters.detail.cond.cautionPassport')
                        : t('scooters.detail.cond.cautionNone')}
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ShieldCheck className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                    <span className="text-sm text-primary">
                      <span className="font-semibold">{t('scooters.detail.cond.bookingDepositLabel')}</span> — {scooter.bookingDepositPercent > 0
                        ? t('scooters.detail.cond.bookingDepositValue', { pct: scooter.bookingDepositPercent })
                        : t('scooters.detail.cond.bookingDepositNone')}
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                    <span className="text-sm text-primary">
                      <span className="font-semibold">{t('scooters.detail.cond.passportLabel')}</span> — {scooter.passport ? t('scooters.detail.cond.passportYes') : t('scooters.detail.cond.passportNo')}
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Fuel className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                    <span className="text-sm text-primary">
                      <span className="font-semibold">{t('scooters.detail.cond.fuelLabel')}</span> — {translateFuelPolicy(scooter.fuelPolicy, i18n.language)}
                    </span>
                  </li>
                </ul>
              </section>

              {/* Localisation (zone uniquement) */}
              <section className="card-dashboard">
                <h2 className="text-xl font-bold text-primary mb-4">{t('scooters.detail.sections.pickupZone')}</h2>
                <div className="relative h-56 rounded-xl overflow-hidden bg-background-soft">
                  <div
                    className="absolute inset-0"
                    style={{
                      background: 'radial-gradient(circle at 40% 45%, rgba(0,184,169,0.35) 0%, rgba(0,184,169,0.15) 30%, rgba(230,242,247,0) 60%), linear-gradient(180deg, #E6F2F7 0%, #F5F7F9 100%)'
                    }}
                  />
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <div className="w-14 h-14 rounded-full bg-accent flex items-center justify-center shadow-lg mb-2">
                      <MapPin className="w-7 h-7 text-white" />
                    </div>
                    <span className="text-lg font-bold text-primary">{scooter.zone}</span>
                    <span className="text-xs text-ink-light">{t('scooters.detail.sections.pickupZoneCountry')}</span>
                  </div>
                </div>
                <div className="mt-4 flex items-start gap-2 text-sm text-ink-light" data-testid="privacy-notice">
                  <Info className="w-4 h-4 shrink-0 mt-0.5 text-accent" />
                  {t('scooters.detail.sections.pickupPrivacyNote')}
                </div>
              </section>

              {/* Avis */}
              {combinedReviews.length > 0 && (
              <section className="card-dashboard" data-testid="reviews-section">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-primary">{t('scooters.detail.sections.reviews')}</h2>
                  {hasReviews && (
                    <div className="flex items-center gap-1">
                      <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                      <span className="font-bold text-primary">{displayAvg}</span>
                      <span className="text-sm text-ink-light">{t('scooters.detail.reviewsCount', { count: displayCount })}</span>
                    </div>
                  )}
                </div>
                <div className="space-y-4" data-testid="reviews-list">
                  {combinedReviews.map((r, i) => (
                    <div key={i} className="border-b border-slate-100 last:border-0 pb-4 last:pb-0" data-testid={`review-${i}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <div className="text-sm font-semibold text-primary flex items-center gap-1">
                            {r.name || r.author}
                            {r.verified && <BadgeCheck className="w-3.5 h-3.5 text-accent" title={t('scooters.detail.verifiedClient')} />}
                          </div>
                          <div className="text-xs text-ink-muted">
                            {r.country ? `${r.country} • ` : ''}
                            {r.date ? formatDateOnly(r.date) : ''}
                          </div>
                        </div>
                        <div className="flex items-center gap-0.5">
                          {[...Array(5)].map((_, idx) => (
                            <Star key={idx} className={`w-3.5 h-3.5 ${idx < r.rating ? 'fill-yellow-400 text-yellow-400' : 'text-slate-200'}`} />
                          ))}
                        </div>
                      </div>
                      <ReviewComment
                        comment={r.comment}
                        translations={r.comment_translations}
                        sourceLang={r.comment_source_lang}
                      />
                    </div>
                  ))}
                </div>
              </section>
              )}
            </div>

            {/* Right column - Booking */}
            <aside className="lg:col-span-1">
              <div className="lg:sticky lg:top-24 card-dashboard">
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-3xl font-bold text-primary" data-testid="scooter-price">{scooter.price}</span>
                  <span className="text-base text-ink-light">{t('scooters.detail.pricePerDay')}</span>
                </div>
                {hasReviews ? (
                  <div className="flex items-center gap-1 text-sm mb-5" data-testid="scooter-sidebar-rating">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    <span className="font-semibold text-primary">{displayAvg}</span>
                    <span className="text-ink-light">{t('scooters.detail.reviewsInline', { count: displayCount })}</span>
                  </div>
                ) : (
                  <div className="mb-5" />
                )}

                {scooter.durationDiscounts && scooter.durationDiscounts.type !== 'none' && (
                  <div className="mb-4">
                    <DiscountBanner basePrice={scooter.price} offer={scooter.durationDiscounts} />
                  </div>
                )}

                <Dialog open={dialogOpen} onOpenChange={(o) => {
                  setDialogOpen(o);
                  if (o && id) {
                    axios.post(`${API}/services/${id}/track`, { event: 'click' }).catch(() => {});
                  }
                }}>
                  <DialogTrigger asChild>
                    <button className="btn-primary w-full mb-3" data-testid="open-booking">
                      {t('scooters.detail.booking.openCta')}
                    </button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="booking-dialog">
                    <DialogHeader>
                      <DialogTitle>{t('scooters.detail.booking.title', { name: scooter.name })}</DialogTitle>
                      <DialogDescription>
                        {t('scooters.detail.booking.subtitle')}
                      </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-5 mt-4">
                      {/* Dates — Calendrier premium */}
                      <div>
                        <h3 className="text-sm font-bold text-primary mb-3 flex items-center gap-2">
                          <CalendarIcon className="w-4 h-4 text-accent" /> {t('scooters.detail.booking.periodTitle')}
                        </h3>
                        <BookingCalendar
                          range={{
                            from: startDate ? new Date(`${startDate}T12:00:00`) : undefined,
                            to: endDate ? new Date(`${endDate}T12:00:00`) : undefined,
                          }}
                          days={days}
                          onChange={(r) => {
                            setStartDate(toLocalYMD(r?.from));
                            setEndDate(toLocalYMD(r?.to));
                          }}
                          blockedDates={blockedDates}
                          partialDates={partialDates}
                        />
                        <div className="grid grid-cols-2 gap-3 mt-3">
                          <div>
                            <Label className="text-xs font-medium text-ink-light mb-1.5 block">{t('scooters.detail.booking.pickupTime')}</Label>
                            <select
                              value={startTime}
                              onChange={(e) => setStartTime(e.target.value)}
                              className="input-field appearance-none"
                              data-testid="booking-start-time"
                            >
                              {HOUR_SLOTS.map((h) => {
                                // If startDate is today (Bangkok), disable past slots.
                                const disabled = (() => {
                                  if (!startDate) return false;
                                  const nowBK = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Bangkok' }));
                                  const todayBK = `${nowBK.getFullYear()}-${String(nowBK.getMonth() + 1).padStart(2, '0')}-${String(nowBK.getDate()).padStart(2, '0')}`;
                                  if (startDate !== todayBK) return false;
                                  const [hh, mm] = h.split(':').map(Number);
                                  return hh * 60 + mm <= nowBK.getHours() * 60 + nowBK.getMinutes();
                                })();
                                return <option key={h} value={h} disabled={disabled}>{h}{disabled ? t('scooters.detail.booking.pastSuffix') : ''}</option>;
                              })}
                            </select>
                          </div>
                          <div>
                            <Label className="text-xs font-medium text-ink-light mb-1.5 block">{t('scooters.detail.booking.returnTime')}</Label>
                            <select
                              value={endTime}
                              onChange={(e) => setEndTime(e.target.value)}
                              className="input-field appearance-none"
                              data-testid="booking-end-time"
                            >
                              {HOUR_SLOTS.map((h) => <option key={h} value={h}>{h}</option>)}
                            </select>
                          </div>
                        </div>
                        <TimezoneNote className="mt-2" />
                      </div>

                      {/* Résumé tarifaire dynamique */}
                      <PriceSummary quote={days > 0 ? quoteState : null}
                        delivery={deliveryFee != null ? { zone: selectedRate.zone_name, fee: deliveryFee } : null} />

                      {/* Livraison optionnelle */}
                      {/* Offered only when the provider really delivers (« delivery_available »). */}
                      {deliveryOffered && (
                        <div className="rounded-xl border border-slate-200 p-4 bg-white">
                          <label className="flex items-start gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={deliveryRequested}
                              onChange={(e) => setDeliveryRequested(e.target.checked)}
                              className="w-4 h-4 rounded text-accent mt-0.5"
                              data-testid="booking-delivery-checkbox"
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <Truck className="w-4 h-4 text-accent" />
                                <span className="text-sm font-semibold text-primary">{t('scooters.detail.booking.deliveryRequested')}</span>
                              </div>
                              <p className="text-xs text-ink-muted mt-0.5">{t(deliveryPriced ? 'deliveryFees.help' : 'scooters.detail.booking.deliveryHelp')}</p>
                            </div>
                          </label>
                          {deliveryRequested && (
                            <div className="mt-4 space-y-4" data-testid="booking-delivery-form">
                              {/* 1. Zone — cards with the provider's real price (delivery + collection). */}
                              <fieldset>
                                <legend className="text-sm font-semibold text-primary mb-2">{t('deliveryFees.zoneQuestion')}</legend>
                                <div role="radiogroup" className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="booking-delivery-zones">
                                  {deliveryRates.map((r) => {
                                    const active = r.zone_id === deliveryZone;
                                    return (
                                      <button
                                        key={r.zone_id}
                                        type="button"
                                        role="radio"
                                        aria-checked={active}
                                        onClick={() => setDeliveryZone(r.zone_id)}
                                        className={`w-full text-left rounded-xl border-2 p-3 flex items-start gap-3 transition-colors ${active ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 bg-white hover:border-accent'}`}
                                        data-testid={`booking-delivery-zone-${r.zone_id}`}
                                      >
                                        <span className={`mt-0.5 w-4 h-4 shrink-0 rounded-full border-2 flex items-center justify-center ${active ? 'border-emerald-500' : 'border-slate-300'}`} aria-hidden="true">
                                          {active && <span className="w-2 h-2 rounded-full bg-emerald-500" />}
                                        </span>
                                        <span className="flex-1 min-w-0">
                                          <span className="block text-sm font-semibold text-primary truncate">{r.zone_name}</span>
                                          <span className="block text-xs text-ink-muted">{t('deliveryFees.roundTrip')}</span>
                                          <span className={`block mt-1 font-bold ${active ? 'text-emerald-700 text-base' : 'text-primary text-sm'}`}>
                                            {Number(r.fee).toLocaleString(numberLocale())} THB
                                          </span>
                                        </span>
                                      </button>
                                    );
                                  })}
                                </div>
                                {!selectedRate && (
                                  <p className="mt-1.5 text-xs text-amber-700" data-testid="booking-delivery-zone-required">{t('deliveryFees.zoneRequired')}</p>
                                )}
                              </fieldset>
                              {/* 2. Accommodation (required). */}
                              <div>
                                <Label htmlFor="booking-delivery-hotel" className="text-xs font-medium text-ink-light mb-1 block">{t('deliveryFees.hotelLabel')}</Label>
                                <Input id="booking-delivery-hotel" value={deliveryHotel} onChange={(e) => setDeliveryHotel(e.target.value)} maxLength={200}
                                  className="input-field" placeholder={t('deliveryFees.hotelPh')} data-testid="booking-delivery-hotel" />
                                {!deliveryHotel.trim() && (
                                  <p className="mt-1 text-xs text-amber-700" data-testid="booking-delivery-hotel-required">{t('deliveryFees.hotelRequired')}</p>
                                )}
                              </div>
                              {/* 3. Google Maps link (optional, recommended): format and domain only. */}
                              <div>
                                <Label htmlFor="booking-delivery-maps" className="text-xs font-medium text-ink-light mb-1 block">{t('deliveryFees.mapsLabel')}</Label>
                                <Input id="booking-delivery-maps" value={deliveryMaps} onChange={(e) => setDeliveryMaps(e.target.value)} inputMode="url" maxLength={600}
                                  className={`input-field ${mapsInvalid ? 'border-red-400' : ''}`} placeholder={t('deliveryFees.mapsPh')} data-testid="booking-delivery-maps" />
                                {mapsInvalid ? (
                                  <p className="mt-1 text-xs text-red-600" data-testid="booking-delivery-maps-invalid">{t('deliveryFees.mapsInvalid')}</p>
                                ) : (
                                  <p className="mt-1 text-xs text-ink-muted">{t('deliveryFees.mapsHelp')}</p>
                                )}
                                <a href="https://www.google.com/maps" target="_blank" rel="noopener noreferrer"
                                  className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline" data-testid="booking-delivery-open-maps">
                                  <MapPin className="w-3.5 h-3.5" /> {t('deliveryFees.openGoogleMaps')}
                                </a>
                              </div>
                              {/* 4. More details (optional). */}
                              <div>
                                <Label htmlFor="booking-delivery-notes" className="text-xs font-medium text-ink-light mb-1 block">{t('deliveryFees.notesLabel')}</Label>
                                <Textarea id="booking-delivery-notes" value={deliveryNotes} onChange={(e) => setDeliveryNotes(e.target.value)} maxLength={1000}
                                  className="input-field min-h-16" placeholder={t('deliveryFees.notesPh')} data-testid="booking-delivery-notes" />
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Informations voyageur */}
                      <div>
                        <h3 className="text-sm font-bold text-primary mb-3 flex items-center gap-2">
                          <User className="w-4 h-4 text-accent" /> {t('scooters.detail.booking.yourInfo')}
                        </h3>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs font-medium text-ink-light mb-1.5 block">{t('scooters.detail.booking.firstName')}</Label>
                            <Input
                              value={firstName}
                              onChange={(e) => setFirstName(e.target.value)}
                              className="input-field"
                              placeholder={t('scooters.detail.booking.firstNamePh')}
                              data-testid="booking-first-name"
                            />
                          </div>
                          <div>
                            <Label className="text-xs font-medium text-ink-light mb-1.5 block">{t('scooters.detail.booking.lastName')}</Label>
                            <Input
                              value={lastName}
                              onChange={(e) => setLastName(e.target.value)}
                              className="input-field"
                              placeholder={t('scooters.detail.booking.lastNamePh')}
                              data-testid="booking-last-name"
                            />
                          </div>
                          <div className="col-span-2">
                            <Label className="text-xs font-medium text-ink-light mb-1.5 block">{t('scooters.detail.booking.email')}</Label>
                            <Input
                              type="email"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              className="input-field"
                              placeholder={t('scooters.detail.booking.emailPh')}
                              data-testid="booking-email"
                            />
                          </div>
                        </div>
                        <p className="text-xs text-ink-muted mt-2 flex items-start gap-1.5">
                          <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                          {t('scooters.detail.booking.contactNote')}
                        </p>
                      </div>

                      {/* Notes */}
                      <div>
                        <Label className="text-sm font-medium mb-2 block">{t('scooters.detail.booking.notes')}</Label>
                        <Textarea
                          value={bookingNotes}
                          onChange={(e) => setBookingNotes(e.target.value)}
                          placeholder={t('scooters.detail.booking.notesPh')}
                          className="input-field min-h-20"
                          data-testid="booking-notes"
                        />
                      </div>

                      {/* Récap prix */}
                      <div className="bg-background-soft rounded-xl p-4">
                        {days > 0 ? (
                          <>
                            {!meetsMinDuration && (
                              <div
                                className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 leading-relaxed"
                                data-testid="booking-min-duration-warning"
                              >
                                <strong>{t('scooters.detail.booking.minDurationWarning', { count: minRentalDuration })}</strong><br />
                                {t('scooters.detail.booking.minDurationBody', { count: minRentalDuration, selected: days })}
                              </div>
                            )}
                            {/* Server quote: daily breakdown, subtotal, the one discount, total */}
                            {!priceSummary && (
                              <p className="text-sm text-ink-muted mb-2" data-testid="booking-total-pending">
                                {quoteState.status === 'error' ? t('scooterPricing.summary.error') : t('scooterPricing.summary.calculating')}
                              </p>
                            )}
                            {priceSummary && priceSummary.daily_breakdown && priceSummary.daily_breakdown.length > 0 && (
                              <>
                                <div className="text-xs text-ink-muted mb-2">
                                  <span className="font-semibold">{t('scooters.detail.booking.dailyBreakdown')}</span>
                                </div>
                                <div className="space-y-1 mb-2 max-h-32 overflow-y-auto" data-testid="booking-daily-breakdown">
                                  {priceSummary.daily_breakdown.map((day, idx) => (
                                    <div key={idx} className="flex items-center justify-between text-xs">
                                      <span className="text-ink-light">{formatDateOnly(day.date)}</span>
                                      <span className="text-ink">{day.price.toLocaleString(numberLocale())} THB</span>
                                    </div>
                                  ))}
                                </div>
                              </>
                            )}
                            {priceSummary && (
                              <>
                                <div className="flex items-center justify-between text-sm mb-1 pt-2 border-t border-slate-200">
                                  <span className="text-ink-light">{t('scooters.detail.booking.subtotal', { count: priceSummary.days })}</span>
                                  <span className="font-semibold text-primary" data-testid="booking-subtotal">{priceSummary.subtotal.toLocaleString(numberLocale())} THB</span>
                                </div>
                                {priceSummary.discount_amount > 0 && (
                                  <div className="flex items-center justify-between text-sm text-green-600 mb-1" data-testid="booking-discount">
                                    <span>
                                      {priceSummary.discount_percentage != null
                                        ? t('scooterPricing.summary.percentDiscount', { pct: formatPct(priceSummary.discount_percentage, numberLocale()), days: priceSummary.discount_min_days })
                                        : t('scooterPricing.summary.fixedDiscount', { days: priceSummary.fixed_tier?.min_days, price: formatTHB(priceSummary.fixed_tier?.daily_price) })}
                                    </span>
                                    <span className="font-semibold">−{priceSummary.discount_amount.toLocaleString(numberLocale())} THB</span>
                                  </div>
                                )}
                              </>
                            )}
                            {deliveryFee != null && totalPrice != null && (
                              <>
                                <div className="flex items-center justify-between text-sm pt-2 border-t border-slate-200" data-testid="booking-rental-line">
                                  <span className="text-ink-light">{t('deliveryFees.rental')}</span>
                                  <span className="font-semibold text-primary">{totalPrice.toLocaleString(numberLocale())} THB</span>
                                </div>
                                <div className="flex items-center justify-between text-sm" data-testid="booking-delivery-line">
                                  <span className="text-ink-light">{t('deliveryFees.feeInZone', { zone: selectedRate.zone_name })}</span>
                                  <span className="font-semibold text-primary">{deliveryFee.toLocaleString(numberLocale())} THB</span>
                                </div>
                              </>
                            )}
                            <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                              <span className="font-bold text-primary">{t(deliveryFee != null ? 'deliveryFees.total' : 'scooters.detail.booking.totalEstimated')}</span>
                              <span className="font-bold text-lg text-primary" data-testid="booking-total">{grandTotalPrice != null ? `${grandTotalPrice.toLocaleString(numberLocale())} THB` : '—'}</span>
                            </div>
                            <div className="mt-3 pt-3 border-t border-slate-200 space-y-1.5 text-xs">
                              {scooter.bookingDepositPercent > 0 && (
                                <div className="flex items-center justify-between" data-testid="booking-summary-acompte">
                                  <span className="text-ink-light">
                                    {t('scooters.detail.booking.summaryAcompte', { pct: scooter.bookingDepositPercent })}
                                    {deliveryFee != null && <> {t('deliveryFees.depositOnRental')}</>}
                                  </span>
                                  <span className="font-semibold text-accent">
                                    {depositAmount != null ? `${depositAmount.toLocaleString(numberLocale())} THB` : '—'}
                                  </span>
                                </div>
                              )}
                              <div className="flex items-center justify-between" data-testid="booking-summary-caution">
                                <span className="text-ink-light">{t('scooters.detail.booking.summaryCaution')}</span>
                                <span className="font-semibold text-primary">
                                  {scooter.securityDeposit?.type === 'fixed' && scooter.securityDeposit.amount > 0
                                    ? `${Number(scooter.securityDeposit.amount).toLocaleString(numberLocale())} THB`
                                    : scooter.securityDeposit?.type === 'passport'
                                    ? t('scooters.detail.booking.passportGuarantee')
                                    : '—'}
                                </span>
                              </div>
                              <div className="text-[11px] text-ink-muted pt-1 leading-relaxed" data-testid="booking-payment-note">
                                {paymentNoteLines({
                                  depositPct: scooter.bookingDepositPercent,
                                  deliveryFee,
                                  formatFee: (n) => n.toLocaleString(numberLocale()),
                                }).map(([key, params], i) => (
                                  <p key={key} data-testid={`booking-payment-note-${key}`}>
                                    {i === 0 && <Info className="w-3 h-3 inline mr-1" />}
                                    {t(`scooters.detail.booking.paymentNote.${key}`, params)}
                                  </p>
                                ))}
                              </div>
                            </div>
                          </>
                        ) : (
                          <p className="text-sm text-ink-light text-center">{t('scooters.detail.booking.selectDatesHint')}</p>
                        )}
                      </div>

                      {/* CGL */}
                      <label className="flex items-start gap-3 cursor-pointer p-3 rounded-xl bg-white border border-slate-200 hover:border-accent transition-colors">
                        <input
                          type="checkbox"
                          checked={acceptCgv}
                          onChange={(e) => setAcceptCgv(e.target.checked)}
                          className="mt-0.5 w-4 h-4 rounded border-slate-300 text-accent focus:ring-accent"
                          data-testid="booking-cgv"
                        />
                        <span className="text-sm text-primary">
                          {t('scooters.detail.booking.acceptPrefix')}
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); setCgvOpen(true); }}
                            className="text-accent underline hover:text-accent-hover"
                            data-testid="open-cgv"
                          >
                            {t('scooters.detail.booking.cgvLink')}
                          </button>
                          .
                        </span>
                      </label>

                      <Button
                        onClick={handleBooking}
                        className="btn-primary w-full h-auto whitespace-normal text-center leading-snug"
                        disabled={submitting || !isFormValid}
                        data-testid="confirm-booking"
                      >
                        {submitting
                          ? t('scooters.detail.booking.submitSending')
                          : (!user ? t('scooters.detail.booking.submitLogin') : t('scooters.detail.booking.submit'))}
                      </Button>
                      <p className="text-xs text-ink-muted text-center">
                        {t('scooters.detail.booking.submitFooter')}
                      </p>
                    </div>
                  </DialogContent>
                </Dialog>

                {/* Confirmation dialog */}
                <BookingRequestSentDialog
                  open={confirmOpen}
                  onOpenChange={setConfirmOpen}
                  bookingId={createdBookingId}
                  continuePath="/scooters"
                />

                <div className="text-xs text-ink-muted text-center">
                  {t('scooters.detail.sidebar.noCommitment')}
                </div>

                <div className="mt-5 pt-5 border-t border-slate-100 space-y-3">
                  {/* Only when the admin has granted the public badge to this provider. */}
                  {scooter.provider.verified && (
                    <div className="flex items-center gap-2 text-sm text-primary" data-testid="sidebar-verified-provider">
                      <BadgeCheck className="w-4 h-4 text-accent" /> {t('scooters.detail.verifiedProvider')}
                    </div>
                  )}
                  {scooter.freeDelivery && (
                    <div className="flex items-center gap-2 text-sm text-primary">
                      <Truck className="w-4 h-4 text-accent" /> {t('scooters.detail.badges.freeDelivery')}
                    </div>
                  )}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>

      {/* Guest login/signup prompt — appears when a non-authenticated visitor
          attempts to submit a booking request. Form state has been persisted
          in localStorage so the user returns to their filled form. */}
      {/* Phones / tablets: fixed booking bar (daily price shown in the booking card; opens the same form). */}
      <MobileBookingBar
        targetTestId="open-booking"
        price={Number(scooter.price) > 0 ? Number(scooter.price).toLocaleString(numberLocale()) : null}
        unit={t('scooters.detail.pricePerDay')}
      />

      <Dialog open={authPromptOpen} onOpenChange={setAuthPromptOpen}>
        <DialogContent className="max-w-md" data-testid="auth-prompt-modal">
          <DialogHeader>
            <DialogTitle>{t('scooters.detail.authPrompt.title')}</DialogTitle>
            <DialogDescription>
              {t('scooters.detail.authPrompt.subtitle')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="rounded-xl bg-background-soft p-3 text-xs text-ink-light">
              <p className="font-semibold text-primary mb-1">{t('scooters.detail.authPrompt.recap')}</p>
              {startDate && endDate ? (
                <p data-testid="auth-prompt-recap">
                  {t('scooters.detail.authPrompt.recapLine', { dates: `${formatDateOnly(startDate)} → ${formatDateOnly(endDate)}`, count: days, price: grandTotalPrice != null ? grandTotalPrice.toLocaleString(numberLocale()) : '—' })}
                </p>
              ) : (
                <p className="italic">{t('scooters.detail.authPrompt.noDates')}</p>
              )}
            </div>
            <Button
              onClick={() => { saveDraft(); navigate(`/login?redirect=/scooters/${scooter.id}`); }}
              className="btn-primary w-full"
              data-testid="auth-prompt-login"
            >
              {t('scooters.detail.authPrompt.login')}
            </Button>
            <Button
              onClick={() => { saveDraft(); navigate(`/register?redirect=/scooters/${scooter.id}`); }}
              variant="outline"
              className="w-full"
              data-testid="auth-prompt-signup"
            >
              {t('scooters.detail.authPrompt.signup')}
            </Button>
            <p className="text-xs text-ink-muted text-center">
              {t('scooters.detail.authPrompt.prefillNote')}
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Conditions Générales de Location */}
      <Dialog open={cgvOpen} onOpenChange={setCgvOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="cgv-modal">
          <DialogHeader>
            <DialogTitle>{t('scooters.detail.cgv.title')}</DialogTitle>
            <DialogDescription>{t('scooters.detail.cgv.subtitle')}</DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto pr-2 text-sm text-primary space-y-4 leading-relaxed" data-testid="cgv-content">
            <section>
              <h4 className="font-semibold text-primary mb-1">{t('scooters.detail.cgv.s1Title')}</h4>
              <p className="text-ink-light">{t('scooters.detail.cgv.s1Body')}</p>
            </section>
            <section>
              <h4 className="font-semibold text-primary mb-1">{t('scooters.detail.cgv.s2Title')}</h4>
              <p className="text-ink-light">{t('scooters.detail.cgv.s2Body')}</p>
            </section>
            <section>
              <h4 className="font-semibold text-primary mb-1">{t('scooters.detail.cgv.s3Title')}</h4>
              <p className="text-ink-light">{t('scooters.detail.cgv.s3Body')}</p>
            </section>
            <section>
              <h4 className="font-semibold text-primary mb-1">{t('scooters.detail.cgv.s4Title')}</h4>
              <p className="text-ink-light">{t('scooters.detail.cgv.s4Body')}</p>
            </section>
            <section>
              <h4 className="font-semibold text-primary mb-1">{t('scooters.detail.cgv.s5Title')}</h4>
              <p className="text-ink-light">{t('scooters.detail.cgv.s5Body')}</p>
            </section>
            <section>
              <h4 className="font-semibold text-primary mb-1">{t('scooters.detail.cgv.s6Title')}</h4>
              <p className="text-ink-light">{t('scooters.detail.cgv.s6Body')}</p>
            </section>
            <section>
              <h4 className="font-semibold text-primary mb-1">{t('scooters.detail.cgv.s7Title')}</h4>
              <p className="text-ink-light">{t('scooters.detail.cgv.s7Body')}</p>
            </section>
            <section>
              <h4 className="font-semibold text-primary mb-1">{t('scooters.detail.cgv.s8Title')}</h4>
              <p className="text-ink-light">{t('scooters.detail.cgv.s8Body')}</p>
            </section>
            <p className="text-xs text-ink-muted italic pt-2 border-t border-slate-100">
              {t('scooters.detail.cgv.footer')}
            </p>
          </div>
          <div className="mt-4 pt-3 border-t border-slate-100 flex justify-end">
            <Button onClick={() => setCgvOpen(false)} className="btn-primary" data-testid="close-cgv">{t('scooters.detail.cgv.close')}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
